import {
	AfterViewInit,
	Component,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { BREAKPOINTS, CONFIG, IMAGE_DETECTOR, IV_THEME_MODES, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { EngineService } from '@core/services/engine/engine.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { ModalService } from '@core/services/modal/modal.service';
import { ArLoadingModalComponent } from '@shared/modals/ar-loading-modal/ar-loading-modal.component';
import { RotateDeviceModalComponent } from '@shared/modals/rotate-device-modal/rotate-device-modal.component';
import {
	BackgroundConfig,
	BackgroundImageModalAction,
	BlindData,
	CropperSettings,
	CropperAction,
	ProjectSamplesImages,
	LoadedImageData,
	PhotoForm,
	BlindCreateAction,
} from '@root/app.interfaces';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { DetectionService } from '@core/services/detection/detection.service';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import Cropper from 'cropperjs';
import * as _ from 'lodash';
import { first, firstValueFrom } from 'rxjs';
import { GtmService } from '@core/services/gtm/gtm.service2';
import { delay, filter } from 'rxjs/operators';
import CropEndEvent = Cropper.CropEndEvent;
import { VariantService } from '@core/services/variant/variant.service';
import { BACKGROUND_MODAL_EVENTS, BgTypes, SBMContext, ScenarioBlindTypes } from '@core/services/gtm/gtm.events2';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { QuizService } from '@core/services/quiz/quiz.service';
import { getScreenOrientation } from '@core/helpers/orientation.utils';

@Component({
	selector: 'app-select-background-modal',
	templateUrl: './select-background-modal.component.html',
	styleUrls: ['./select-background-modal.component.scss', './select-background-modal.responsive.scss'],
})
export class SelectBackgroundModalComponent implements OnInit, AfterViewInit, OnDestroy {
	static readonly ID = crypto.randomUUID();

	protected readonly BlindCreateAction = BlindCreateAction;

	private readonly NODE_NAME = {
		nw: 'top_left',
		n: 'top',
		ne: 'top_right',
		w: 'left',
		all: 'move',
		move: 'move',
		e: 'right',
		sw: 'bottom_left',
		s: 'bottom',
		se: 'bottom_right',
		crop: 'new_crop',
		zoom: 'zoom',
	};
	private readonly ZOOM_DEBOUNCE_MS = 400;

	@ViewChild('formElement') formElement: ElementRef;
	@ViewChild('photoInput') photoInput: ElementRef<HTMLInputElement>;
	@ViewChild('containerElement') containerElement: ElementRef<HTMLElement>;
	@ViewChild('fakeScrollBar') fakeScrollBar: ElementRef<HTMLElement>;
	@ViewChild('fakeScrollBarThumb') fakeScrollBarThumb: ElementRef<HTMLElement>;
	@ViewChildren('fixedBottomButton') fixedBottomButton: QueryList<ElementRef<HTMLButtonElement>>;
	@ViewChildren('slickModal') slickModal: QueryList<SlickCarouselComponent>;

	breakpoints = BREAKPOINTS;
	isMobile: boolean = this.passDataService.isMobileOrTablet;
	isImageVisualisation: boolean;
	isLoading = false;
	isSubmitting = false;
	getBlindData: BlindData[] | null;
	getProductType: string;
	selectedBackgroundType = '';
	selectedProductType = '';
	allowedMimeType: string[] = ['image/png', 'image/jpeg', 'image/jpg'];
	maxFileSize = 10000000;
	maxSizeError = false;
	fileTypeError = false;
	abortController: AbortController;
	imageWidth: number;
	imageHeight: number;
	sampleStatus: boolean;
	sampleType: string;
	sampleIndex: number;
	sampleSavedSettings;

	containerResizeObserver: ResizeObserver;

	form: FormGroup<PhotoForm> = this.fb.group({
		photo: new FormControl<any>(null),
		photo_base64: new FormControl<string | null>(null),
		photo_src: new FormControl<string | null>(null),
		photo_blob: new FormControl<Blob | null>(null),
		cropped_src: new FormControl<string | null>(null),
		night_src: new FormControl<string | null>(null),
	});

	originalImageBase64: string | ArrayBuffer;
	originalCropperSettings: CropperSettings;

	cropper: Cropper;
	cropperBaseZoomRatio: number;
	cropperAspectRatio = 16 / 9;
	cropperCanvasOptions: Cropper.GetCroppedCanvasOptions = {
		maxWidth: 3072,
		maxHeight: 3072,
	};
	cropperConfig: Cropper.Options = {
		viewMode: 0,
		restore: false,
		dragMode: 'move',
		movable: true,
		cropBoxMovable: false,
		toggleDragModeOnDblclick: false,
	};
	readonly CropperAction = CropperAction;

	projectSamples: ProjectSamplesImages;

	slideConfig = {
		slidesToShow: 3,
		slidesToScroll: 3,
		dots: false,
		infinite: false,
		variableWidth: true,
		mobileFirst: false,
		arrows: false,
		responsive: [
			{
				breakpoint: CONFIG.breakpoints['phone'],
				settings: {
					slidesToShow: 1.5,
					slidesToScroll: 1,
				},
			},
		],
	};

	progressBar = {
		id: '',
		progress: 0,
		chars_received: null,
		content_length: null,
	};

	isShop = false;

	isSameImage = true;

	private cropInteraction?: {
		node: string;
		start: Cropper.Data;
	};

	private zoomSession?: {
		startRatio: number;
		lastRatio: number;
		ticks: number;
		timer?: number;
	};

	@HostListener('window:orientationchange', ['$event']) onOrientationChange() {
		if (this.selectedBackgroundType === 'project_sample' && this.form.value.photo_base64) {
			this.onSelectProjectSample(null, this.sampleType, this.sampleIndex);
		}
	}

	@HostListener('window:resize', ['$event']) onResize() {
		this.setImageCropperAspectRatio();
		setTimeout(this.setImageCropperToImageSize.bind(this));
	}

	constructor(
		private fb: UntypedFormBuilder,
		private engineService: EngineService,
		private shareService: ShareService,
		private setupPageService: SetupPageService,
		private sessionStorageService: SessionStorageService,
		private passDataService: PassDataService,
		private modalService: ModalService,
		private matDialog: MatDialog,
		private indexedDBService: IndexedDBService,
		public dialogRef: MatDialogRef<SelectBackgroundModalComponent>,
		private detectionService: DetectionService,
		private gtm: GtmService,
		private variant: VariantService,
		private blindStateService: BlindStateService,
		private quizService: QuizService,

		@Inject(MAT_DIALOG_DATA) public data: { context: SBMContext; blind_detection: boolean; action: BackgroundImageModalAction },
	) {}

	ngOnInit() {
		console.log('Shop variant:', this.variant.isShop());
		this.isShop = this.variant.isShop();
		this.onResize();
		this.engineService.setGizmoControlVisible(false);
		this.modalService.liftUpModal(this.dialogRef);
		this.backgroundImagesHandler();
		this.getCurrentBlindData();
		this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.open, { context: this.data.context }); // Not sure if to keep this or not as other events will imply that the modal is being opened however events such as the view switcher might not always open this modal. Also the event name is sbm_interaction despite the user not always having to interact to trigger this event.
	}

	ngAfterViewInit() {
		this.updateFakeScrollBarHeight();
		this.containerResizeHandler();
	}

	ngOnDestroy() {
		this.engineService.setGizmoControlVisible(false);
		this.containerResizeObserver?.disconnect();
	}

	getCurrentBlindData(): void {
		this.getProductType = this.setupPageService.getProductType();
		this.getBlindData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!this.getBlindData) {
			this.dialogRef.disableClose = true;
			this.getProductType = this.setupPageService.getProductType();

			this.onSelectProductType(this.getProductType === 'all' ? 'outdoor' : this.getProductType);
			this.onSelectType('project_sample');
			this.onSelectProjectSample(null, this.selectedProductType, 1);
		} else if (this.shareService.getSelectOpeningModeValue()) {
			this.onSelectType('upload');
		}
	}

	backgroundImagesHandler(): void {
		const storageConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);

		if (storageConfig) {
			this.projectSamples = { ...storageConfig?.iv_images };
		}
	}

	onSelectProductType(type: string, buttonPress = false) {
		this.selectedProductType = type;

		if (this.selectedBackgroundType === 'project_sample') {
			this.onSelectProjectSample(null, this.selectedProductType, 1);
		}

		if (buttonPress) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM[type + '_blinds'], { context: this.data.context });
		}
	}

	onSelectType(type: string, buttonPress = false) {
		const getProductType = this.setupPageService.getProductType();
		const screenType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type);
		this.selectedBackgroundType = type;
		this.scrollToForm();
		this.onDeleteFile();

		this.isImageVisualisation = screenType === VIEW_TYPES.image_visualisation && getProductType === 'all';

		if (this.selectedBackgroundType === 'project_sample') {
			this.removeImageCropper();

			const isSampleReady = (this.sampleType && this.sampleIndex) || this.getBlindData;

			if (isSampleReady) {
				this.onSelectProjectSample(null, this.sampleType || 'outdoor', this.sampleIndex || 1);
			}
		}

		if (this.selectedBackgroundType === 'upload') {
			this.abortController?.abort();

			if (this.data?.action !== BackgroundImageModalAction.Add) {
				this.setUploadedBackground();
			}
		}

		if (this.selectedBackgroundType === 'no_background') {
			this.setNoBackground();
		}

		this.handleBackgroundTypeChange();

		if (buttonPress) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM[type], { context: this.data.context });
		}
	}

	onSelectProjectSample(event: Event, type: string, index: number, buttonPress = false) {
		const target = event ? (event.currentTarget as HTMLElement) : null;

		this.selectedProductType = type;
		this.sampleStatus = true;
		this.sampleType = type;
		this.sampleIndex = index;

		this.form.controls.photo.reset();
		this.form.controls.photo_base64.reset();
		this.form.controls.photo_src.reset();

		if (target) {
			const sampleList = document.querySelectorAll(SELECTORS.background_project_samples);
			sampleList.forEach((element: HTMLElement) => element.classList.remove('active'));

			target.classList.add('active');
		}

		this.abortController?.abort();
		this.abortController = new AbortController();
		const signal = this.abortController.signal;
		const orientation = getScreenOrientation();
		const id = `${type}_${index}_${orientation}`;

		this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, id).then((image) => {
			if (image) {
				this.form.controls.night_src.setValue(image.night_src);
				this.uploadFile(image.blob);
			} else {
				this.getSelectProjectImages(type, index, orientation, signal);
			}

			this.slickModal.get(Number(type === 'interior')).slickGoTo(index);
			this.scrollToElement(this.formElement.nativeElement.querySelector('button[type=submit]'));
		});

		if (buttonPress) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM[`${type}_sample_${index + 1}`], { context: this.data.context });
		}
	}

	setNoBackground(): void {
		// There is no need to clear the sampleType and sampleIndex fields here.
		// Due to incorrect display of the 'Use a sample project' tab.
		this.abortController?.abort();
		this.form.controls.photo.reset();
		this.form.controls.photo_base64.reset();
		this.form.controls.photo_src.reset();
	}

	getSelectProjectImages(type: string, index: number, orientation: string, signal: AbortSignal): void {
		this.isLoading = true;

		const isOutdoor = type === 'outdoor';
		const selectProjectPromises = [
			this.getImage(signal, this.projectSamples[type].day[orientation][index]),
			(isOutdoor || this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code) === 'sg') &&
				this.getImage(signal, this.projectSamples[type].night[orientation][index]),
		];

		this.clearProgressBar();

		Promise.all(selectProjectPromises).then(([dayBlob, nightBlob]) => {
			this.form.controls.night_src.setValue(nightBlob ? window.URL.createObjectURL(nightBlob) : null);
			this.uploadFile(dayBlob);
		});
	}

	async getImage(signal: AbortSignal, url: string): Promise<Blob> {
		const response = await fetch(url, { signal });
		const reader = response.body.getReader();
		const chunks = [];
		this.progressBar.content_length += +response.headers.get('content-length');

		const process = ({ done, value }) => {
			if (done) {
				this.clearProgressBar();
				return;
			}

			this.progressBar = {
				...this.progressBar,
				chars_received: this.progressBar.chars_received + value.length,
				id: `${this.sampleType}_${this.sampleIndex}`,
				progress: Math.round((this.progressBar.chars_received / this.progressBar.content_length) * 100),
			};
			chunks.push(value);

			return reader.read().then(process.bind(this));
		};

		await reader.read().then(process.bind(this));

		return new Blob(chunks);
	}

	clearProgressBar(): void {
		this.progressBar = {
			id: '',
			progress: 0,
			chars_received: null,
			content_length: null,
		};
	}

	private buildDelta(a: Cropper.Data, b: Cropper.Data) {
		const dx = Math.round(b.x - a.x);
		const dy = Math.round(b.y - a.y);
		const dw = Math.round(b.width - a.width);
		const dh = Math.round(b.height - a.height);
		return { dx, dy, dw, dh };
	}

	createImageCropper(cropperSettings?: CropperSettings): void {
		if (this.form.value.photo_base64 && this.selectedBackgroundType === 'upload') {
			const image = document.getElementById(SELECTORS.cropper_image) as HTMLImageElement;

			const ready = () => {
				setTimeout(
					() => this.formElement.nativeElement.scrollIntoView({ top: this.formElement.nativeElement.scrollHeight, behavior: 'smooth' }),
					150,
				);

				const image = this.cropper.getImageData();
				this.cropperBaseZoomRatio = image.width / image.naturalWidth;

				if (!cropperSettings) {
					this.setImageCropperToImageSize();
					this.resetFormPristineOnCropChange();
					this.isSameImage = this.isImageUnchanged();
					return;
				}

				const containerData = this.cropper.getContainerData();
				const { cropper_container_data, cropper_data, cropper_box_data, cropper_canvas_data } = cropperSettings;

				if (_.isEqual(containerData, cropper_container_data)) {
					this.cropper.setData(cropper_data).setCropBoxData(cropper_box_data).setCanvasData(cropper_canvas_data);
				} else {
					this.setImageCropperToImageSize();
					this.form.markAsDirty();
				}
			};

			const zoom = (e: Cropper.ZoomEvent) => {
				const zoomRatio = e.detail.ratio;
				const zoomDifference = this.cropperBaseZoomRatio / zoomRatio;

				if (zoomDifference > 2 || zoomDifference < 0.2) {
					e.preventDefault();
				} else {
					this.form.markAsDirty();
				}

				const oldR = e.detail.oldRatio ?? zoomRatio;

				if (!e.detail.originalEvent) {
					const from = oldR;
					const to = zoomRatio;
					const pctOfReady = (to / this.cropperBaseZoomRatio) * 100;
					const oldPctOfReady = (from / this.cropperBaseZoomRatio) * 100;
					const deltaPctOfReady = pctOfReady - oldPctOfReady;

					if (deltaPctOfReady >= 0) {
						this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER.zoom_in, {
							context: this.data.context,
							zoom_method: 'button',
							zoom_percent: +pctOfReady.toFixed(2),
							d_zoom_percent: +deltaPctOfReady.toFixed(2),
						});
					} else {
						this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER.zoom_out, {
							context: this.data.context,
							zoom_method: 'button',
							zoom_percent: +pctOfReady.toFixed(2),
							d_zoom_percent: +deltaPctOfReady.toFixed(2),
						});
					}

					return;
				}

				if (!this.zoomSession) {
					this.zoomSession = {
						startRatio: oldR,
						lastRatio: zoomRatio,
						ticks: 1,
					};
				} else {
					this.zoomSession.lastRatio = zoomRatio;
					this.zoomSession.ticks += 1;
				}

				// reset debounce timer
				if (this.zoomSession.timer) {
					clearTimeout(this.zoomSession.timer);
				}

				this.zoomSession.timer = window.setTimeout(() => {
					const s = this.zoomSession!;
					const from = s.startRatio;
					const to = s.lastRatio;
					const pctOfReady = (to / this.cropperBaseZoomRatio) * 100;
					const oldPctOfReady = (from / this.cropperBaseZoomRatio) * 100;
					const deltaPctOfReady = pctOfReady - oldPctOfReady;

					if (deltaPctOfReady >= 0) {
						this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER.zoom_in, {
							context: this.data.context,
							zoom_method: 'wheel/pinch',
							zoom_percent: +pctOfReady.toFixed(2),
							d_zoom_percent: +deltaPctOfReady.toFixed(2),
						});
					} else {
						this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER.zoom_out, {
							context: this.data.context,
							zoom_method: 'wheel/pinch',
							zoom_percent: +pctOfReady.toFixed(2),
							d_zoom_percent: +deltaPctOfReady.toFixed(2),
						});
					}

					this.zoomSession = undefined;
				}, this.ZOOM_DEBOUNCE_MS);
			};

			const cropstart = (event: Cropper.CropStartEvent) => {
				const rawAction = (event?.detail?.action as string) || 'move';
				const node = this.NODE_NAME[rawAction as keyof typeof this.NODE_NAME] ?? 'move';
				this.cropInteraction = {
					node,
					start: this.cropper.getData(true),
				};
			};

			const cropend = (event: CropEndEvent) => {
				const rawAction = (event?.detail?.action as string) || 'none';
				if (!['all', 'none'].includes(event.detail.action as Cropper.Action | 'none')) {
					this.form.markAsDirty();
					this.resetFormPristineOnCropChange();
				}

				const end = this.cropper.getData(true);

				this.centerCropBoxAndImage();

				const node = this.cropInteraction?.node || this.NODE_NAME[rawAction as keyof typeof this.NODE_NAME] || 'move';

				const start = this.cropInteraction?.start ?? end; // fallback prevents NaNs
				const delta = this.buildDelta(start, end); // returns 0s for dx & dy for right or bottom crops

				if (node === 'move') {
					this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER.move, { context: this.data.context, ...delta });
				} else {
					this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER['crop_' + node], { context: this.data.context, ...delta });
				}

				this.cropInteraction = undefined;
			};

			this.cropper?.destroy();
			this.cropper = new Cropper(image, { ...this.cropperConfig, ready, zoom, cropstart, cropend });
		}
	}

	centerCropBoxAndImage(): void {
		const containerData = this.cropper.getContainerData();
		const canvasData = this.cropper.getCanvasData();
		const imageData = this.cropper.getImageData();
		const cropBoxData = this.cropper.getCropBoxData();

		// CENTER CROPBOX LOGIC
		// calculate the center of the crop box
		const cropBoxCenterX = cropBoxData.left + cropBoxData.width / 2;
		const cropBoxCenterY = cropBoxData.top + cropBoxData.height / 2;

		// calculate the center of the container
		const containerCenterX = containerData.width / 2;
		const containerCenterY = containerData.height / 2;

		// calculate the difference between the centers
		const deltaX = containerCenterX - cropBoxCenterX;
		const deltaY = containerCenterY - cropBoxCenterY;

		// move the crop box by the difference
		this.cropper.setCropBoxData({
			left: cropBoxData.left + deltaX,
			top: cropBoxData.top + deltaY,
		});

		// CENTER CROPED IMAGE LOGIC
		// calculate the difference between the width of container and image
		const imageDeltaX = containerData.width - imageData.width;
		const imageDeltaY = containerData.height - imageData.height;

		// calculate the left and top position of the cropped area
		const croppedLeft = canvasData.left + cropBoxData.left - canvasData.width / 2 + cropBoxData.width / 2;
		const croppedTop = canvasData.top + cropBoxData.top - canvasData.height / 2 + cropBoxData.height / 2;

		// calculate the left and top position of the cropped image
		const imgLeft = canvasData.left - croppedLeft + imageDeltaX / 2 - imageData.left;
		const imgTop = canvasData.top - croppedTop + imageDeltaY / 2 - imageData.top;

		this.cropper.setCanvasData({
			left: canvasData.left + imgLeft,
			top: canvasData.top + imgTop,
		});
	}

	setImageCropperAspectRatio(): void {
		const wrapper = document.querySelector(SELECTORS.wrapper);
		this.cropperAspectRatio = wrapper.clientWidth / wrapper.clientHeight;
	}

	setImageCropperToImageSize(): void {
		if (this.cropper) {
			const canvasData = this.cropper.getCanvasData();
			this.cropper.setCropBoxData({ height: canvasData.height, width: canvasData.width, top: canvasData.top, left: canvasData.left });
		}
	}

	removeImageCropper(): void {
		this.form.controls.cropped_src.reset();
		this.cropper?.destroy();
		this.cropper = null;
	}

	onImageCropperButton(type: CropperAction): void {
		switch (type) {
			case CropperAction.UPLOAD:
				this.photoInput.nativeElement.click();
				break;
			case CropperAction.ROTATE:
				this.cropper.rotate(90);
				break;
			case CropperAction.ZOOM_IN:
				this.cropper.zoom(0.1);
				break;
			case CropperAction.ZOOM_OUT:
				this.cropper.zoom(-0.1);
				break;
			case CropperAction.RESET:
				this.cropper.reset();
				this.setImageCropperToImageSize();
				break;
			case CropperAction.DELETE:
				this.onDeleteFile();
				break;
		}

		this.form.markAsDirty();
		this.resetFormPristineOnCropChange();

		if (type === CropperAction.DELETE) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.delete, { context: this.data.context });
		} else if (type === CropperAction.UPLOAD) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.upload_new, { context: this.data.context });
		} else if (type === CropperAction.RESET || type === CropperAction.ROTATE) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.CROPPER[type.toLowerCase()], { context: this.data.context });
		}
	}

	uploadPhotoBtnClicked() {
		this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.upload_photo, { context: this.data.context });
	}

	onSelectFile(event: Event) {
		const input = event.target as HTMLInputElement;
		const fileArray = this.convertFileList(input.files);

		if (fileArray.length) {
			this.sampleType = null;
			this.sampleIndex = null;
			this.uploadFileToPromise(fileArray[0]).then(() => {
				this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.photo_uploaded, { context: this.data.context, upload_method: 'dialog' });
				this.createImageCropper();
			});
		}

		if (input.id === 'photo') {
			this.scrollToForm();
		}
	}

	onDropFile(event) {
		this.preventDefaultAndStopPropagation(event);
		event.target.classList.remove('drag-over');

		const fileArray = this.convertFileList(event.dataTransfer.files);

		if (fileArray.length) {
			this.uploadFileToPromise(fileArray[0]).then(() => {
				this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.photo_uploaded, { context: this.data.context, upload_method: 'drag_drop' });
				this.createImageCropper();
			});
		}
	}

	setUploadedBackground() {
		const ivBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_uploaded_image_visualisation_background);

		if (ivBackgroundImage) {
			this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, ivBackgroundImage.id).then((image) => {
				this.sampleType = null;
				this.sampleIndex = null;
				this.sampleSavedSettings = {
					settings: ivBackgroundImage.settings,
					shutterValues: ivBackgroundImage.shutterValues,
				};

				if (image) {
					this.uploadFileToPromise(image.blob).then(() => {
						this.originalImageBase64 = this.form.value.photo_base64;
						this.originalCropperSettings = image.cropper_settings;

						this.createImageCropper(image.cropper_settings);
					});
				}
			});
		}
	}

	onDragEvent(event: Event) {
		this.preventDefaultAndStopPropagation(event);

		if (event.type === 'dragover') {
			(event.target as HTMLElement).classList.add('drag-over');
		} else if (event.type === 'dragleave') {
			(event.target as HTMLElement).classList.remove('drag-over');
		}

		this.form.markAsDirty();
	}

	preventDefaultAndStopPropagation(event: Event) {
		event.preventDefault();
		event.stopPropagation();
	}

	convertFileList(files: FileList): File[] {
		this.fileTypeError = false;
		this.maxSizeError = false;

		return [...files].filter((file: File) => {
			const isAllowedType = this.allowedMimeType.some((type: string) => type === file.type);
			const isCorrectFileSize = file.size <= this.maxFileSize;

			if (!isAllowedType) {
				this.fileTypeError = true;
			} else if (!isCorrectFileSize) {
				this.maxSizeError = true;
			}

			return isAllowedType && isCorrectFileSize;
		});
	}

	onLoadImage(file: File | Blob): Promise<LoadedImageData> {
		return new Promise((resolve, reject) => {
			const image = new Image();
			image.src = window.URL.createObjectURL(file);
			image.onload = () => {
				resolve({
					src: image.src,
					height: image.height,
					width: image.width,
				});
			};
			image.onerror = (error) => reject(error);
		});
	}

	uploadFile(file: File | Blob, resolve?: () => void) {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			this.isLoading = false;
			this.form.controls.photo_base64.setValue(reader.result);
		};

		if (resolve) {
			// Activate AI detection, start a node for detection
			this.detectionService.activateSystem();

			reader.onloadend = () => resolve();
		}

		this.onLoadImage(file).then((loadedImage) => {
			this.form.controls.photo_src.setValue(loadedImage.src);
			this.form.controls.photo_blob.setValue(file);
			this.imageHeight = loadedImage.height;
			this.imageWidth = loadedImage.width;
		});
	}

	uploadFileToPromise(file: File | Blob) {
		return new Promise<void>((resolve) => this.uploadFile(file, resolve));
	}

	onDeleteFile(type?: string) {
		this.form.controls.photo.reset();
		this.form.controls.photo_base64.reset();
		this.form.controls.photo_src.reset();
		this.fileTypeError = false;
		this.maxSizeError = false;

		if (type === 'photo') {
			this.selectedBackgroundType = '';
			this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_iv_type);
		}
	}

	onSubmit() {
		if (!this.form.valid) {
			return;
		}

		this.sessionStorageService.setSession(this.selectedBackgroundType, STORAGE_NAMES.zip_iv_type);
		this.isLoading = true;
		this.isSubmitting = true;

		if (this.cropper) {
			this.cropper.getCroppedCanvas(this.cropperCanvasOptions).toBlob((file) => {
				if (this.isImageUnchanged() && !this.getBlindData?.length) {
					void this.onSubmitCallback(this.setBackgroundConfig.bind(this));
					return;
				}

				if (this.imageDetectionStatus()) {
					void this.detectionService.startDetection(file);
				}

				this.onLoadImage(file).then((loadedImage) => {
					this.form.controls.cropped_src.setValue(loadedImage.src);
					this.imageHeight = loadedImage.height;
					this.imageWidth = loadedImage.width;
					this.sessionStorageService.setSession(
						{ width: this.imageWidth, height: this.imageHeight },
						STORAGE_NAMES.zip_detection_image_size,
					);
					void this.onSubmitCallback(this.setBackgroundConfig.bind(this));
				});
			});
		} else {
			if (this.selectedBackgroundType === 'upload') {
				// Not sure this condition is ever met
				void this.detectionService.startDetection(this.form.value.photo_blob);
				this.sessionStorageService.setSession({ width: this.imageWidth, height: this.imageHeight }, STORAGE_NAMES.zip_detection_image_size);
			}

			void this.onSubmitCallback(this.setBackgroundConfig.bind(this), this.selectedBackgroundType === 'project_sample');
		}

		if (this.data.context === 'welcome' || !this.isSameImage || this.gtm.scenarioBgType !== this.selectedBackgroundType) {
			this.gtm.startScenario(this.selectedProductType as ScenarioBlindTypes, this.selectedBackgroundType as BgTypes, this.data.context);
		} else {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.SCENARIO_UPDATE.edit_image, { context: this.data.context });
		}
	}

	setBackgroundConfig(): void {
		const backgroundImage = this.selectedBackgroundType !== 'no_background' ? this.getBackgroundConfig() : null;

		if (!this.sampleType && backgroundImage) {
			this.sampleSavedSettings = this.isImageUnchanged() ? this.sampleSavedSettings : {};
			backgroundImage['settings'] = this.sampleSavedSettings.settings;
			backgroundImage['shutterValues'] = this.sampleSavedSettings.shutterValues;
		}

		// onClose() is called first to avoid incorrect display of Quiz Popup
		this.onClose(this.imageDetectionStatus());
		this.closeRotateDeviceModal();
		this.engineService.setBackgroundImage(backgroundImage);

		this.passDataService.isLiveOpened = false;
	}

	getBackgroundConfig(): BackgroundConfig {
		const orientation = getScreenOrientation();
		const imageOrientation = this.imageWidth > this.imageHeight ? 'landscape' : 'portrait';
		const cropperSettings = this.getCropperSettings();

		return {
			src: this.form.value.photo_src,
			night_src: this.form.value.night_src,
			blob: this.form.value.photo_blob,
			base64: this.form.value.photo_base64,
			cropped_src: this.form.value.cropped_src,
			cropper_settings: cropperSettings,
			width: this.imageWidth,
			height: this.imageHeight,
			theme: IV_THEME_MODES.day,
			type: this.sampleType,
			index: this.sampleIndex,
			image_orientation: this.sampleType ? orientation : imageOrientation,
			id: this.sampleType ? `${this.sampleType}_${this.sampleIndex}_${orientation}` : 'own_image',
		};
	}

	getCropperSettings(): CropperSettings {
		return {
			cropper_data: this.cropper?.getData(),
			cropper_box_data: this.cropper?.getCropBoxData(),
			cropper_canvas_data: this.cropper?.getCanvasData(),
			cropper_container_data: this.cropper?.getContainerData(),
		};
	}

	imageDetectionStatus(): boolean {
		return IMAGE_DETECTOR === 'off'
			? false
			: this.selectedBackgroundType === 'upload' && (!this.isImageUnchanged() || !this.getBlindData?.length);
	}

	isImageUnchanged(): boolean {
		const cropperSettings = this.getCropperSettings();

		return this.originalImageBase64 === this.form.value.photo_base64 && _.isEqual(this.originalCropperSettings, cropperSettings);
	}

	resetFormPristineOnCropChange(): void {
		if (this.isImageUnchanged() && !this.engineService.sampleImage.type) {
			this.form.markAsPristine();
		}
	}

	onBlindTypeSelect(type: string): number {
		const id = this.setupPageService.generateUniqueBlindId();

		if (this.sampleType) {
			this.passDataService.autoCreatedBlindId = id;
		}

		this.shareService.getModelLoaded.pipe(first()).subscribe(this.engineService.createScene.bind(this.engineService, type, id.toString()));

		this.sessionStorageService.setSession(type, STORAGE_NAMES.zip_blind_type);
		this.shareService.setBlindType({ type, id });
		return id;
	}

	onClose(selectOpeningMode: boolean = null, buttonPress: boolean = false): void {
		this.quizService.showQuizIfNeeded(this.selectedBackgroundType, selectOpeningMode);
		if (selectOpeningMode) {
			this.shareService.setSelectOpeningMode(true);
			this.engineService.toggleAllRootMeshes(false);
		} else if (selectOpeningMode === false) {
			this.shareService.setSelectOpeningMode(false);
			this.engineService.toggleAllRootMeshes(true);
		}
		this.dialogRef.close(selectOpeningMode !== null);

		if (buttonPress) {
			this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.close, { context: this.data.context });
		}
	}

	scrollToForm() {
		if (
			this.formElement &&
			((getScreenOrientation() === 'portrait' && window.innerWidth <= CONFIG.breakpoints['phone']) ||
				(getScreenOrientation() === 'landscape' && window.innerWidth <= CONFIG.breakpoints['tablet-landscape']))
		) {
			setTimeout(this.scrollToElement.bind(this, this.formElement.nativeElement));
		}
	}

	trackByIndex(index: number): number {
		return index;
	}

	openLive(): void {
		this.selectedBackgroundType = 'live';
		const dialog = this.matDialog.open(ArLoadingModalComponent, this.modalService.getConfig(ArLoadingModalComponent.ID));
		const addVideoLayer = () => {
			this.engineService
				.addVideoLayer()
				.then(() => {
					this.onClose(false);
					dialog.close();
				})
				.catch(() => {
					this.getBlindData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
					this.engineService.videoLayer.dispose();
					dialog.close();
				});
		};

		// Should reset before opening Live mode.
		this.sampleType = null;
		this.sampleIndex = null;

		this.gtm.pushEvent(BACKGROUND_MODAL_EVENTS.GENERIC_SBM.live_background, { context: this.data.context });

		void this.onSubmitCallback(addVideoLayer);
	}

	async onSubmitCallback(callback: () => void, isSampleProject = false): Promise<void> {
		const sampleProject = this.engineService.getIVCurrentSampleProject(this.sampleType, this.sampleIndex);
		const currentTypeBlindsCount = this.getBlindData?.filter((blind) => blind.type === this.selectedProductType).length || 0;
		const shouldCreateSameTypeBlinds = currentTypeBlindsCount < sampleProject?.length;
		const extraBlindsCount = currentTypeBlindsCount ? currentTypeBlindsCount - sampleProject?.length : 0;

		if ((!this.getBlindData || shouldCreateSameTypeBlinds) && this.selectedProductType) {
			const blindsCount = isSampleProject ? sampleProject.length - currentTypeBlindsCount : 1;
			this.createBlindList(blindsCount).then(async () => {
				const lastChangedBlind = this.getLastChangedBlind();
				await this.deleteUnchangedAutoBlinds(extraBlindsCount);
				callback();
				if (lastChangedBlind?.type === this.sampleType) {
					setTimeout(this.shareService.setAccordionBlindById.bind(this.shareService, lastChangedBlind.blind_id), 500);
				}
			});
		} else {
			const lastChangedBlind = this.getLastChangedBlind();
			const hasUnConfiguredBlind = this.getBlindData.some((blind) => !blind.meta?.change_time);
			const canOpenLastChangedBlind = !this.isShop || (this.isShop && !hasUnConfiguredBlind);
			const promise = firstValueFrom(
				this.shareService.getBboxApplied.pipe(
					filter((id) => +id === lastChangedBlind?.blind_id),
					first(),
				),
			);
			await this.deleteUnchangedAutoBlinds(extraBlindsCount);
			callback();
			if (
				canOpenLastChangedBlind &&
				lastChangedBlind?.type === this.sampleType &&
				!this.isBlindOpenInBlindList(lastChangedBlind.blind_id)
			) {
				promise.then(this.shareService.setAccordionBlindById.bind(this.shareService, lastChangedBlind.blind_id));
			}
		}
	}

	async deleteUnchangedAutoBlinds(extraBlindsCount: number): Promise<void> {
		if (this.getBlindData?.length) {
			const sameTypeUnchangedAutoBlindsIds = [];
			const productType = this.selectedProductType || this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_type);
			const firstFiltered: BlindData[] = [];

			for (let i = 0; i < this.getBlindData.length; i++) {
				const blind = this.getBlindData[i];
				const { is_auto_created, change_time } = blind.meta;

				if (!is_auto_created || change_time) {
					firstFiltered.push(blind);
				}

				if (is_auto_created && !change_time) {
					if (blind.type !== productType) {
						this.shareService.setBlindDelete(blind.blind_id);
						await firstValueFrom(this.shareService.getSafeToFit.pipe(first()));
					} else {
						sameTypeUnchangedAutoBlindsIds.push(blind.blind_id);
						firstFiltered.push(blind);
					}
				}
			}

			if (!this.sampleType && !extraBlindsCount) {
				// If we are not in sample mode (upload mode), if there is already a blind made or modified by the user, we remove all auto created (and unchanged) blinds else we keep one of the auto created (and unchanged) blinds.
				extraBlindsCount =
					firstFiltered.length > sameTypeUnchangedAutoBlindsIds.length
						? sameTypeUnchangedAutoBlindsIds.length
						: sameTypeUnchangedAutoBlindsIds.length - 1;
			}

			const blindsIdForRemove = sameTypeUnchangedAutoBlindsIds.splice(0, extraBlindsCount);
			const secondFiltered: BlindData[] = [];

			for (let i = 0; i < firstFiltered.length; i++) {
				const blind = this.getBlindData[i];
				const blindForRemove = blindsIdForRemove.includes(blind.blind_id);
				if (blindForRemove) {
					this.shareService.setBlindDelete(blind.blind_id);
					await Promise.all([
						firstValueFrom(this.shareService.getSafeToFit.pipe(first())),
						firstValueFrom(
							this.shareService.getBboxApplied.pipe(
								filter((id) => +id !== blind.blind_id),
								first(),
								delay(50),
							),
						),
					]);
				} else {
					secondFiltered.push(blind);
				}
			}

			this.getBlindData = secondFiltered;
		}
	}

	async createBlindList(blindsCount: number): Promise<void> {
		const sameTypeBlindsIds = (this.getBlindData || []).filter((blind) => blind.type === this.sampleType).map((blind) => blind.blind_id);

		for (let i = 0; i < blindsCount; i++) {
			const blindId = this.onBlindTypeSelect(this.selectedProductType);

			if (!this.getBlindData?.length && (!sameTypeBlindsIds.length || !this.isImageVisualisation)) {
				await firstValueFrom(this.shareService.getBlindSizeChanged.pipe(first()));
			} else {
				await firstValueFrom(
					this.shareService.getBboxApplied.pipe(
						filter((id) => +id === blindId),
						first(),
					),
				);
			}
		}

		if (this.isShop) {
			const blind = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data)[0];
			this.blindStateService.openNextSection(blind.blind_id);
		}
	}

	closeRotateDeviceModal(): void {
		const dialog = this.matDialog.getDialogById(RotateDeviceModalComponent.ID);

		if (dialog) {
			dialog.close();
		}
	}

	scrollToElement(element: HTMLElement): void {
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}

	handleBackgroundTypeChange(): void {
		const prevBackgroundType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type);

		if (this.selectedBackgroundType !== prevBackgroundType) {
			this.form.markAsDirty();
		}
	}

	containerResizeHandler(): void {
		if (!this.containerElement?.nativeElement || this.containerResizeObserver) {
			return;
		}
		this.containerResizeObserver = new ResizeObserver(() => {
			this.onContainerScroll();
			this.updateFakeScrollBarHeight();
		});
		this.containerResizeObserver.observe(this.containerElement.nativeElement);
	}

	onContainerScroll(): void {
		if (!this.fakeScrollBar?.nativeElement) {
			return;
		}
		const container = this.containerElement.nativeElement;
		const scrollRatio = container.scrollTop / (container.scrollHeight - container.clientHeight);
		const offsetBottom = this.getFakeScrollBarOffsetBottom();
		const thumbMaxTop = container.clientHeight - this.fakeScrollBarThumb.nativeElement.clientHeight - offsetBottom;
		this.fakeScrollBarThumb.nativeElement.style.top = `${scrollRatio * thumbMaxTop}px`;
	}

	updateFakeScrollBarHeight(): void {
		if (this.fakeScrollBar?.nativeElement) {
			const containerHeight = this.containerElement.nativeElement.clientHeight;
			const contentHeight = this.containerElement.nativeElement.scrollHeight;
			const offsetBottom = this.getFakeScrollBarOffsetBottom();

			this.fakeScrollBarThumb.nativeElement.style.height = `${Math.max((containerHeight / contentHeight) * containerHeight, 20) - offsetBottom}px`;
			this.fakeScrollBar.nativeElement.style.height = `${containerHeight - offsetBottom}px`;
		}
	}

	getFakeScrollBarOffsetBottom(): number {
		return Math.max(...Array.from(this.fixedBottomButton).map((button) => button.nativeElement.clientHeight));
	}

	getLastChangedBlind(): BlindData | null {
		if (!this.getBlindData) {
			return null;
		}
		return [...this.getBlindData]
			.filter((blind) => blind.meta?.change_time)
			.sort((blind1, blind2) => new Date(blind2.meta?.change_time).getTime() - new Date(blind1.meta?.change_time).getTime())[0];
	}

	isBlindOpenInBlindList(id: number): boolean {
		return document.getElementById(id.toString())?.classList.contains('active');
	}
}
